import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { getTiposCambio, setTipoCambioManual, refrescarBCU, borrarTipoCambio, MONEDAS_BCU } from '../../lib/contabilidadApi'

const ISOS = Object.keys(MONEDAS_BCU)
const BCU_URL = 'https://www.bcu.gub.uy/Estadisticas-e-Indicadores/Paginas/Cotizaciones.aspx'

export default function TiposCambio() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtroIso, setFiltroIso] = useState('USD')
  const [refrescando, setRefrescando] = useState(false)
  const [modal, setModal] = useState(null)
  const [mensaje, setMensaje] = useState(null)

  useEffect(() => { load() }, [filtroIso])

  async function load() {
    setLoading(true)
    setItems(await getTiposCambio({ iso: filtroIso || undefined }))
    setLoading(false)
  }

  async function refrescar() {
    setRefrescando(true); setMensaje(null)
    try {
      const r = await refrescarBCU({ monedas: filtroIso ? [filtroIso] : ['USD'] })
      const ok = r.resultados?.find(x => x.ok)
      const primerError = r.resultados?.find(x => x.error)?.error
      setMensaje(ok
        ? { tipo: 'ok', txt: r.mensaje_usuario || `BCU OK: ${ok.iso} compra ${ok.compra}` }
        : { tipo: 'warn', txt: r.mensaje_usuario || primerError || 'BCU no devolvió datos. Usá "+ Cargar manual".' })
      load()
    } catch (e) { setMensaje({ tipo: 'error', txt: e.message }) }
    finally { setRefrescando(false) }
  }

  async function guardar(form) {
    try {
      await setTipoCambioManual(form)
      setModal(null)
      setMensaje({ tipo: 'ok', txt: `TC ${form.codigo_iso} del ${form.fecha} guardado manual.` })
      load()
    } catch (e) { alert('Error: ' + e.message) }
  }

  async function borrar(t, e) {
    e?.stopPropagation()
    if (!confirm(`¿Borrar la cotización ${t.codigo_iso} del ${t.fecha}? Si era una de prueba, mejor sacarla para que no contamine los asientos.`)) return
    try {
      await borrarTipoCambio(t.fecha, t.codigo_iso)
      setMensaje({ tipo: 'ok', txt: `Borrado: ${t.codigo_iso} ${t.fecha}.` })
      load()
    } catch (err) { setMensaje({ tipo: 'error', txt: err.message }) }
  }

  const stats = useMemo(() => {
    const ultimo = items[0]
    const manuales = items.filter(t => t.fuente === 'manual').length
    const auto = items.filter(t => t.fuente === 'bcu_api').length
    return { ultimo, manuales, auto, total: items.length }
  }, [items])

  return (
    <AdminLayout>
      <div className="tc-wrap">
        <div className="tc-headline">
          <div>
            <h1>Tipos de cambio BCU</h1>
            <p>Cotización oficial para asientos contables. Regla Roberto: <b>interbancario billete comprador del día anterior</b>.</p>
          </div>
          <div className="tc-actions">
            <a className="tc-btn-bcu" href={BCU_URL} target="_blank" rel="noopener noreferrer" title="Abrir cotizaciones oficiales BCU">
              🏛 Abrir BCU
            </a>
            <button className="tc-btn-ghost" onClick={refrescar} disabled={refrescando}>
              {refrescando ? '⏳ Pidiendo BCU…' : '↻ Refrescar desde BCU'}
            </button>
            <button className="tc-btn-primary" onClick={() => setModal({ codigo_iso: filtroIso || 'USD', fecha: new Date().toISOString().slice(0,10) })}>
              + Cargar manual
            </button>
          </div>
        </div>

        {mensaje && (
          <div className={`tc-banner tc-banner-${mensaje.tipo}`}>
            {mensaje.txt}
            {mensaje.tipo === 'warn' && (
              <> · <a href={BCU_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#78350f', fontWeight: 800 }}>Abrir BCU ↗</a></>
            )}
          </div>
        )}

        <div className="tc-kpis">
          {stats.ultimo && (
            <div className="tc-kpi tc-kpi-big">
              <div className="tc-kpi-label">{stats.ultimo.codigo_iso} · último</div>
              <div className="tc-kpi-val">${stats.ultimo.tc_interbancario_comprador ?? stats.ultimo.tc_compra ?? '—'}</div>
              <div className="tc-kpi-sub">{stats.ultimo.fecha} · {stats.ultimo.fuente}</div>
            </div>
          )}
          <Kpi label="Registros" val={stats.total} />
          <Kpi label="Carga BCU auto" val={stats.auto} color="#16a34a" />
          <Kpi label="Carga manual" val={stats.manuales} color="#d97706" />
        </div>

        <div className="tc-toolbar">
          <select value={filtroIso} onChange={(e) => setFiltroIso(e.target.value)}>
            <option value="">Todas las monedas</option>
            {ISOS.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>

        {loading ? <div className="tc-loading">Cargando…</div> : (
          <div className="tc-tabla-wrap">
            <table className="tc-tabla">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>ISO</th>
                  <th style={{ textAlign: 'right' }}>Compra billete</th>
                  <th style={{ textAlign: 'right' }}>Venta billete</th>
                  <th style={{ textAlign: 'right' }}>Interbanc. compra<br /><small>★ asiento</small></th>
                  <th style={{ textAlign: 'right' }}>Interbanc. venta</th>
                  <th>Fuente</th>
                  <th>Notas</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map(t => (
                  <tr key={`${t.fecha}-${t.cod_moneda_bcu}`} onClick={() => setModal(t)}>
                    <td style={{ fontWeight: 700, color: '#1A3461' }}>{t.fecha}</td>
                    <td><span className="tc-pill-iso">{t.codigo_iso}</span></td>
                    <td style={{ textAlign: 'right' }}>{t.tc_compra ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>{t.tc_venta ?? '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 800, color: '#0d9488' }}>{t.tc_interbancario_comprador ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>{t.tc_interbancario_vendedor ?? '—'}</td>
                    <td>
                      {t.fuente === 'bcu_api' ? <span className="tc-pill-auto">BCU auto</span>
                        : t.fuente === 'manual' ? <span className="tc-pill-manual">manual</span>
                        : t.fuente}
                    </td>
                    <td style={{ fontSize: 11, color: '#64748b' }}>{t.notas || ''}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="tc-btn-del" onClick={(e) => borrar(t, e)} title="Borrar este item">×</button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>Sin registros. Cargá manualmente o refrescá desde BCU.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="tc-info">
          <strong>📚 Cómo se usa</strong>
          <p>El TC de los asientos USD → UYU sale de <code>tc_para_asiento(fecha, 'USD')</code> que devuelve el <b>interbancario comprador del día ANTERIOR</b> a la fecha del asiento.</p>
          <p>El cron <code>bcu_fetch_tc_diario</code> corre L-V 14:30 UY (BCU publica 13-14 hs). Si falla, usá <b>Cargar manual</b>.</p>
        </div>
      </div>

      {modal && <ModalTC initial={modal} onClose={() => setModal(null)} onSave={guardar} />}

      <style>{CSS}</style>
    </AdminLayout>
  )
}

function Kpi({ label, val, color }) {
  return (
    <div className="tc-kpi" style={{ borderColor: (color || '#94a3b8') + '40' }}>
      <div className="tc-kpi-label" style={{ color: color || '#475569' }}>{label}</div>
      <div className="tc-kpi-val">{val}</div>
    </div>
  )
}

function ModalTC({ initial, onClose, onSave }) {
  const [f, setF] = useState({
    fecha: initial.fecha || new Date().toISOString().slice(0, 10),
    codigo_iso: initial.codigo_iso || 'USD',
    tc_compra: initial.tc_compra ?? '',
    tc_venta: initial.tc_venta ?? '',
    tc_interbancario_comprador: initial.tc_interbancario_comprador ?? '',
    tc_interbancario_vendedor: initial.tc_interbancario_vendedor ?? '',
    notas: initial.notas || '',
  })
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value })
  const upNum = (k) => (e) => setF({ ...f, [k]: e.target.value === '' ? '' : Number(e.target.value) })

  function submit(e) {
    e.preventDefault()
    if (!f.fecha || !f.codigo_iso) return alert('Completá fecha y moneda')
    if (!f.tc_interbancario_comprador && !f.tc_compra) return alert('Cargá al menos compra o interbancario comprador')
    onSave({ ...f })
  }

  return (
    <div className="tc-modal-bg" onClick={onClose}>
      <div className="tc-modal" onClick={(e) => e.stopPropagation()}>
        <button className="tc-modal-close" onClick={onClose}>×</button>
        <h2>Carga manual de TC</h2>
        <div style={{ marginBottom: 14, padding: '8px 12px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12.5, color: '#78350f' }}>
          📋 Abrí el BCU oficial y copiá la cotización: <a href={BCU_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#92400e', fontWeight: 800 }}>🏛 bcu.gub.uy/Cotizaciones ↗</a>
          <br /><span style={{ fontSize: 11 }}>El valor que va al asiento es <b>Compra</b> billete (Roberto criterio #1).</span>
        </div>
        <form onSubmit={submit}>
          <div className="tc-form-row">
            <label className="tc-field">
              <span>Fecha *</span>
              <input type="date" value={f.fecha} onChange={up('fecha')} required />
            </label>
            <label className="tc-field">
              <span>Moneda *</span>
              <select value={f.codigo_iso} onChange={up('codigo_iso')}>
                {ISOS.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </label>
          </div>
          <div className="tc-form-row">
            <label className="tc-field">
              <span>TC compra billete</span>
              <input type="number" step="0.01" value={f.tc_compra} onChange={upNum('tc_compra')} placeholder="41.50" />
            </label>
            <label className="tc-field">
              <span>TC venta billete</span>
              <input type="number" step="0.01" value={f.tc_venta} onChange={upNum('tc_venta')} placeholder="42.10" />
            </label>
          </div>
          <div className="tc-form-row">
            <label className="tc-field">
              <span>★ Interbanc. comprador (asiento)</span>
              <input type="number" step="0.001" value={f.tc_interbancario_comprador} onChange={upNum('tc_interbancario_comprador')} placeholder="41.85" />
            </label>
            <label className="tc-field">
              <span>Interbanc. vendedor</span>
              <input type="number" step="0.001" value={f.tc_interbancario_vendedor} onChange={upNum('tc_interbancario_vendedor')} placeholder="41.95" />
            </label>
          </div>
          <label className="tc-field">
            <span>Notas</span>
            <input type="text" value={f.notas} onChange={up('notas')} placeholder="Ej: BCU no respondió, cargado de cotizaciones-uruguay.com.uy" />
          </label>
          <div className="tc-form-actions">
            <button type="button" className="tc-btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="tc-btn-primary">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  )
}

const CSS = `
.tc-wrap { padding: 16px 20px 32px; max-width: 1300px; margin: 0 auto }
.tc-headline { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; flex-wrap: wrap; margin-bottom: 14px }
.tc-headline h1 { font-size: 24px; font-weight: 900; color: #1A3461; margin: 0 0 4px; letter-spacing: -.02em }
.tc-headline p { font-size: 13px; color: #64748b; margin: 0 }
.tc-actions { display: flex; gap: 8px; flex-wrap: wrap }

.tc-banner { padding: 9px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; font-weight: 600 }
.tc-banner-ok { background: #dcfce7; border: 1px solid #86efac; color: #166534 }
.tc-banner-warn { background: #fef3c7; border: 1px solid #fde68a; color: #92400e }
.tc-banner-error { background: #fee2e2; border: 1px solid #fca5a5; color: #991b1b }

.tc-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 9px; margin-bottom: 12px }
.tc-kpi { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 9px; padding: 9px 12px }
.tc-kpi-big { grid-column: span 2 }
.tc-kpi-label { font-size: 10.5px; text-transform: uppercase; font-weight: 800; letter-spacing: .04em }
.tc-kpi-val { font-size: 22px; font-weight: 900; color: #1A3461 }
.tc-kpi-sub { font-size: 11px; color: #64748b; margin-top: 2px }

.tc-toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 10px }
.tc-toolbar select { padding: 6px 11px; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 12.5px; font-family: inherit; color: #1A3461 }

.tc-loading { padding: 40px; text-align: center; color: #94a3b8 }

.tc-tabla-wrap { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: auto }
.tc-tabla { width: 100%; border-collapse: collapse; font-size: 12.5px }
.tc-tabla th { text-align: left; padding: 8px 11px; background: #f8fafc; font-weight: 700; color: #475569; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e2e8f0 }
.tc-tabla th small { display: block; font-size: 9px; color: #0d9488; text-transform: none; letter-spacing: 0; font-weight: 700 }
.tc-tabla td { padding: 7px 11px; border-bottom: 1px solid #f1f5f9 }
.tc-tabla tbody tr { cursor: pointer }
.tc-tabla tbody tr:hover { background: #f8fafc }

.tc-pill-iso { display: inline-block; padding: 2px 8px; border-radius: 5px; font-size: 10.5px; font-weight: 800; background: #dbeafe; color: #1e3a8a }
.tc-pill-auto { display: inline-block; padding: 2px 7px; border-radius: 5px; font-size: 10px; font-weight: 700; background: #dcfce7; color: #166534 }
.tc-pill-manual { display: inline-block; padding: 2px 7px; border-radius: 5px; font-size: 10px; font-weight: 700; background: #fef3c7; color: #92400e }

.tc-info { margin-top: 18px; padding: 13px 16px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; font-size: 12px; color: #1e3a8a; line-height: 1.55 }
.tc-info strong { font-size: 12.5px }
.tc-info b { color: #1e40af }
.tc-info code { background: #dbeafe; padding: 1px 5px; border-radius: 4px; font-size: 11px }

.tc-btn-primary { background: #1A3461; color: #fff; border: 0; padding: 8px 16px; border-radius: 8px; font-size: 12.5px; font-weight: 800; cursor: pointer; font-family: inherit }
.tc-btn-primary:hover { background: #0f2147 }
.tc-btn-primary:disabled { background: #94a3b8; cursor: not-allowed }
.tc-btn-secondary { background: #fff; border: 1px solid #cbd5e1; color: #475569; padding: 8px 16px; border-radius: 8px; font-size: 12.5px; font-weight: 700; cursor: pointer; font-family: inherit }
.tc-btn-ghost { background: transparent; border: 1px solid #cbd5e1; color: #64748b; padding: 6px 11px; border-radius: 7px; font-size: 11.5px; font-weight: 700; cursor: pointer; font-family: inherit }
.tc-btn-ghost:hover { background: #f1f5f9; color: #1A3461 }
.tc-btn-del { background: transparent; border: 1px solid #fecaca; color: #b91c1c; padding: 2px 8px; border-radius: 5px; font-weight: 900; cursor: pointer; font-size: 13px; line-height: 1 }
.tc-btn-del:hover { background: #fee2e2 }
.tc-btn-bcu { background: #fef3c7; border: 1px solid #fde68a; color: #78350f; padding: 8px 14px; border-radius: 8px; font-size: 12.5px; font-weight: 800; cursor: pointer; font-family: inherit; text-decoration: none; display: inline-flex; align-items: center; gap: 4px }
.tc-btn-bcu:hover { background: #fde68a; border-color: #fbbf24 }

.tc-modal-bg { position: fixed; inset: 0; background: rgba(15,31,61,.55); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 16px }
.tc-modal { background: #fff; border-radius: 14px; max-width: 600px; width: 100%; padding: 24px 26px; position: relative }
.tc-modal h2 { margin: 0 0 16px; font-size: 19px; color: #1A3461; font-weight: 900 }
.tc-modal-close { position: absolute; top: 12px; right: 12px; background: none; border: 0; font-size: 22px; cursor: pointer; color: #64748b }

.tc-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 11px }
.tc-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px }
.tc-field > span { font-size: 11.5px; font-weight: 700; color: #475569 }
.tc-field input, .tc-field select { padding: 8px 10px; border: 1px solid #cbd5e1; border-radius: 7px; font-size: 13px; font-family: inherit; color: #1A3461 }
.tc-form-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 12px; padding-top: 12px; border-top: 1px solid #e2e8f0 }
`
